/**
 * MyApprovalsPage — Artist-facing read-only review dashboard
 * ==========================================================
 * Founder spec Feb 2026 (post-rollout of strict RBAC on /reviews):
 * Artists (employees, freelancers, vendor L1/L2/L3) are gated OUT of
 * the supervisor review tool. Until today, the only place they could
 * see producer feedback was a tiny "Producer Notes" chip on each My
 * Tasks row — text-only, no spatial pins.
 *
 * This page surfaces:
 *   • Every submission the artist owns (either submitted themselves
 *     or assigned to them on the linked task)
 *   • Per-submission status pill + supervisor / PM decisions
 *   • Per-submission detail panel with full annotation pin list
 *     (timecodes, text, author) in a READ-ONLY timeline.
 *
 * Artists cannot annotate / approve / revise — every write endpoint
 * is gated on STUDIO_REVIEWERS in reviews_api.py. This page is the
 * sole artist-side consumer of /api/reviews/my-submissions and
 * /api/reviews/my-submissions/{id}.
 *
 * Phase 3 (AI Layout Supervisor) will drop additional pins with
 * `author_role == "ai_supervisor"` — they'll appear in this same
 * timeline with a distinct purple badge so the artist sees the AI's
 * Disney-grade critique alongside human notes.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Eye, Loader2, Clock, Check, X, AlertTriangle, MessageSquare,
  Film, ChevronRight, Filter, Calendar, User, Star,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import SideBySideReviewPlayer from '@/components/SideBySideReviewPlayer';

// Founder spec (Feb 2026, Phase 1B): when the artist's primary skill
// is animator / layout_artist, "My Approvals" opens the SyncSketch
// side-by-side player (CBA reference on left, their playblast on right,
// pins overlaid spatially). For all other artists, the simpler text +
// pin-timeline modal is shown.
const SIDEBYSIDE_SKILLS = new Set([
  'animator', 'layout_artist', 'layout', 'animation',
  'animation_artist', 'character_animator',
]);
const _isAnimatorLike = (user) => {
  if (!user) return false;
  const role = (user.role || '').toLowerCase();
  if (role === 'animator' || role === 'layout_artist') return true;
  const skills = (user.skills || []).map(s => String(s).toLowerCase());
  return skills.some(s => SIDEBYSIDE_SKILLS.has(s));
};

const STATUS_META = {
  draft:             { label: 'DRAFT',          cls: 'bg-zinc-800 text-zinc-300 border-zinc-700' },
  submitted:         { label: 'SUBMITTED',      cls: 'bg-violet-500/15 text-violet-300 border-violet-500/40' },
  supervisor_review: { label: 'SUPERVISOR QC',  cls: 'bg-blue-500/15 text-blue-300 border-blue-500/40' },
  internal_review:   { label: 'SUPERVISOR QC',  cls: 'bg-blue-500/15 text-blue-300 border-blue-500/40' },
  pending_client:    { label: 'WITH CLIENT',    cls: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/40' },
  client_review:     { label: 'WITH CLIENT',    cls: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/40' },
  revise:            { label: 'REVISE',         cls: 'bg-amber-500/15 text-amber-300 border-amber-500/40' },
  rejected:          { label: 'REJECTED',       cls: 'bg-rose-500/15 text-rose-300 border-rose-500/40' },
  approved:          { label: 'APPROVED',       cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' },
};

const FILTERS = [
  { key: 'all',      label: 'All' },
  { key: 'pending',  label: 'Pending Review' },
  { key: 'revise',   label: 'Needs Revision' },
  { key: 'approved', label: 'Approved' },
];

function _statusPill(status) {
  const m = STATUS_META[status] || { label: (status || 'UNKNOWN').toUpperCase(), cls: 'bg-zinc-800 text-zinc-300 border-zinc-700' };
  return (
    <span className={`px-2 py-0.5 text-[10px] font-bold border rounded-full ${m.cls}`}>
      {m.label}
    </span>
  );
}

function _decisionPill(decision) {
  const map = {
    approved: { cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40', icon: Check },
    revise:   { cls: 'bg-amber-500/15 text-amber-300 border-amber-500/40',     icon: AlertTriangle },
    rejected: { cls: 'bg-rose-500/15 text-rose-300 border-rose-500/40',         icon: X },
  };
  const m = map[(decision || '').toLowerCase()];
  if (!m) return null;
  const Ic = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold border rounded ${m.cls}`}>
      <Ic className="w-2.5 h-2.5" /> {decision.toUpperCase()}
    </span>
  );
}

function _formatTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function _formatTimecode(secs) {
  if (secs == null || isNaN(secs)) return null;
  const s = Math.floor(secs);
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

// ─── Detail panel — DISPATCHER ──────────────────────────────────
// Animators (and layout artists) get the SyncSketch side-by-side
// player. Everyone else gets the simpler text + pin-timeline modal.
function SubmissionDetailPanel({ submissionId, useSideBySide, onClose }) {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!submissionId) return;
    setLoading(true);
    setError(null);
    api.get(`/reviews/my-submissions/${submissionId}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [submissionId, api]);

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center">
        <div className="flex items-center text-zinc-400 text-xs">
          <Loader2 className="w-4 h-4 animate-spin mr-2" />
          Loading review details…
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6"
           onClick={onClose}>
        <div className="bg-red-500/10 border border-red-500/30 text-red-200 rounded p-4 text-xs max-w-md"
             onClick={e => e.stopPropagation()}>
          <AlertTriangle className="w-4 h-4 inline mr-2" />
          {error}
        </div>
      </div>
    );
  }
  if (!data) return null;

  // ── Animator path → SyncSketch side-by-side ──
  if (useSideBySide) {
    return <SideBySideReviewPlayer detail={data} onClose={onClose} />;
  }

  // ── Default path → text + pin-timeline modal ──
  return <TextDetailModal data={data} onClose={onClose} />;
}

// ─── Text-only detail modal (original implementation) ────────────
function TextDetailModal({ data, onClose }) {
  if (!data) return null;

  const { submission, events, annotations, task_context } = data;
  const media = (submission.media || submission.file_urls || []);

  // Sort pins by timecode for video, by created_at otherwise
  const pinsSorted = [...annotations].sort((a, b) => {
    const ta = a.timecode_sec ?? 999999;
    const tb = b.timecode_sec ?? 999999;
    return ta - tb;
  });

  // Group events by round so we show "Round 1: supervisor said X, PM said Y" cleanly
  const eventsByRound = {};
  for (const ev of events) {
    const r = ev.round || submission.round || 1;
    if (!eventsByRound[r]) eventsByRound[r] = [];
    eventsByRound[r].push(ev);
  }
  const rounds = Object.keys(eventsByRound).map(Number).sort((a, b) => b - a);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-6"
      data-testid="my-approvals-detail-modal"
      onClick={onClose}
    >
      <div
        className="bg-zinc-950 border border-zinc-800 rounded-lg max-w-5xl w-full my-6 overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-zinc-800 bg-gradient-to-r from-zinc-900 to-zinc-950 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              {_statusPill(submission.status)}
              <span className="text-[10px] text-zinc-500">
                Round {submission.round || 1}
              </span>
            </div>
            <h2 className="text-xl font-bold text-white">
              {task_context?.title || task_context?.name || submission.title || 'Submission'}
            </h2>
            {task_context && (
              <p className="text-xs text-zinc-400 mt-0.5">
                {task_context.project_name}
                {task_context.shot_code && (
                  <> · <span className="font-mono text-zinc-300">{task_context.shot_code}</span></>
                )}
                {task_context.task_type && (
                  <> · {task_context.task_type.toUpperCase()}</>
                )}
              </p>
            )}
            <p className="text-[10px] text-zinc-500 mt-1">
              Submitted {_formatTime(submission.submitted_at)}
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="my-approvals-detail-close"
            className="text-zinc-500 hover:text-zinc-200 p-1"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
          {/* Media preview (left, 3 cols) */}
          <div className="lg:col-span-3 p-5 border-r border-zinc-800 bg-zinc-950">
            <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
              <Film className="w-3 h-3" /> Submitted Media
            </h3>
            {media.length === 0 && (
              <div className="bg-zinc-900 border border-zinc-800 rounded p-4 text-xs text-zinc-400">
                No media available for this submission.
              </div>
            )}
            {media.map((m, i) => {
              const url = typeof m === 'string' ? m : (m.url || m.file_url || '');
              const lower = url.toLowerCase();
              const isVideo = /\.(mp4|webm|mov|m4v)(\?|$)/.test(lower);
              const isImage = /\.(jpe?g|png|webp|gif)(\?|$)/.test(lower);
              return (
                <div key={i} className="mb-3" data-testid={`my-approvals-media-${i}`}>
                  {isVideo && (
                    <video src={url} controls className="w-full rounded border border-zinc-800" />
                  )}
                  {isImage && (
                    <img src={url} alt="" className="w-full rounded border border-zinc-800" />
                  )}
                  {!isVideo && !isImage && (
                    <a href={url} target="_blank" rel="noopener noreferrer"
                       className="block bg-zinc-900 border border-zinc-800 rounded p-3 text-xs text-blue-300 hover:bg-zinc-800">
                      Open {url.split('/').pop()}
                    </a>
                  )}
                </div>
              );
            })}
          </div>

          {/* Decisions + pins (right, 2 cols) */}
          <div className="lg:col-span-2 p-5 bg-zinc-900/40 overflow-y-auto max-h-[80vh]">
            {/* Producer decisions per round */}
            {rounds.length > 0 && (
              <section className="mb-5">
                <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                  Supervisor / PM Decisions
                </h3>
                {rounds.map(r => (
                  <div key={r} className="mb-3 last:mb-0">
                    <div className="text-[10px] text-zinc-600 mb-1">Round {r}</div>
                    {eventsByRound[r].map((ev, idx) => {
                      const a = (ev.action || '').toLowerCase();
                      const isSup = a.startsWith('supervisor_') || a.startsWith('internal_');
                      const isPM = a.startsWith('pm_');
                      const isAI = a.startsWith('ai_');
                      const decision = a.replace(/^(supervisor_|pm_|internal_|ai_)/, '');
                      const phaseLabel = isAI ? 'Supervisor' : isPM ? 'PM' : isSup ? 'Supervisor' : 'Reviewer';
                      const phaseIcon = isAI ? Star : isPM ? User : Eye;
                      const Ph = phaseIcon;
                      return (
                        <div key={idx}
                             data-testid={`my-approvals-decision-${r}-${idx}`}
                             className={`p-2.5 rounded border ${isAI ? 'border-amber-500/40 bg-amber-500/5' : 'border-zinc-800 bg-zinc-900/50'} mb-1.5 last:mb-0`}>
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="flex items-center gap-1.5">
                              <Ph className={`w-3 h-3 ${isAI ? 'text-amber-300' : 'text-zinc-400'}`} />
                              <span className={`text-[10px] font-bold ${isAI ? 'text-amber-200' : 'text-zinc-300'}`}>
                                {phaseLabel}
                              </span>
                              {ev.actor_name && (
                                <span className="text-[10px] text-zinc-500">· {ev.actor_name}</span>
                              )}
                            </div>
                            {_decisionPill(decision)}
                          </div>
                          {ev.note && (
                            <p className="text-xs text-zinc-200 leading-snug whitespace-pre-wrap">{ev.note}</p>
                          )}
                          <p className="text-[9px] text-zinc-600 mt-1">{_formatTime(ev.at)}</p>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </section>
            )}

            {/* Annotation pins */}
            <section>
              <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
                <MessageSquare className="w-3 h-3" /> Frame Notes ({pinsSorted.length})
              </h3>
              {pinsSorted.length === 0 ? (
                <div className="text-xs text-zinc-500 italic p-3 bg-zinc-900/30 rounded">
                  No frame-specific notes were left on this submission.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {pinsSorted.map((pin, idx) => {
                    const isAI = (pin.author_role || '').toLowerCase().includes('ai');
                    const tc = _formatTimecode(pin.timecode_sec);
                    return (
                      <div key={pin.id || idx}
                           data-testid={`my-approvals-pin-${idx}`}
                           className={`p-2 rounded border ${isAI ? 'border-amber-500/40 bg-amber-500/5' : 'border-zinc-800 bg-zinc-900/50'}`}>
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="flex items-center gap-1.5 text-[10px]">
                            {isAI && <Star className="w-3 h-3 text-amber-300" />}
                            {tc && (
                              <span className="font-mono text-amber-300 bg-amber-500/10 px-1 rounded">
                                @ {tc}
                              </span>
                            )}
                            <span className={`font-bold ${isAI ? 'text-amber-200' : 'text-zinc-300'}`}>
                              {pin.author_name || (isAI ? 'Supervisor' : 'Reviewer')}
                            </span>
                          </div>
                          {pin.resolved && (
                            <span className="text-[9px] text-emerald-400 font-bold">RESOLVED</span>
                          )}
                        </div>
                        <p className="text-xs text-zinc-200 leading-snug whitespace-pre-wrap">
                          {pin.text || <em className="text-zinc-500">(no text)</em>}
                        </p>
                        <p className="text-[9px] text-zinc-600 mt-1">{_formatTime(pin.created_at)}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main list page ──────────────────────────────────────────────
export default function MyApprovalsPage() {
  const { api, user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [openSubmissionId, setOpenSubmissionId] = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    api.get('/reviews/my-submissions')
      .then(r => setItems(r.data?.items || []))
      .catch(e => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (filter === 'all') return items;
    return items.filter(s => {
      const st = (s.status || '').toLowerCase();
      if (filter === 'pending') {
        return ['submitted', 'supervisor_review', 'internal_review',
                'pending_client', 'client_review'].includes(st);
      }
      if (filter === 'revise')  return st === 'revise' || st === 'rejected';
      if (filter === 'approved') return st === 'approved';
      return true;
    });
  }, [items, filter]);

  const counts = useMemo(() => {
    const c = { all: items.length, pending: 0, revise: 0, approved: 0 };
    for (const s of items) {
      const st = (s.status || '').toLowerCase();
      if (['submitted', 'supervisor_review', 'internal_review',
           'pending_client', 'client_review'].includes(st)) c.pending++;
      if (st === 'revise' || st === 'rejected') c.revise++;
      if (st === 'approved') c.approved++;
    }
    return c;
  }, [items]);

  return (
    <div className="space-y-4" data-testid="my-approvals-page">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-white">My Approvals</h1>
        <p className="text-xs text-zinc-400 mt-0.5">
          {user?.full_name ? `${user.full_name.split(' ')[0]}, see ` : 'See '}
          every supervisor / PM note left on your submissions. Click a row
          to read frame-by-frame critique. Read-only — feedback is delivered
          here within minutes of your upload.
        </p>
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-1.5 border-b border-zinc-800 pb-2">
        <Filter className="w-3 h-3 text-zinc-500 mr-1" />
        {FILTERS.map(f => (
          <button
            key={f.key}
            data-testid={`my-approvals-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`px-2.5 py-1 text-[11px] font-bold rounded transition ${filter === f.key
              ? 'bg-amber-400 text-zinc-950'
              : 'bg-zinc-900 text-zinc-300 hover:bg-zinc-800 border border-zinc-800'}`}
          >
            {f.label}
            <span className="ml-1 opacity-70">{counts[f.key]}</span>
          </button>
        ))}
        <div className="flex-1" />
        <button
          onClick={load}
          data-testid="my-approvals-refresh"
          className="px-2 py-1 text-[10px] text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 rounded"
        >
          Refresh
        </button>
      </div>

      {/* List */}
      {loading && (
        <div className="flex items-center justify-center py-16 text-zinc-500 text-xs">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading your reviews…
        </div>
      )}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-200 rounded p-3 text-xs">
          <AlertTriangle className="w-4 h-4 inline mr-2" />
          {error}
        </div>
      )}
      {!loading && !error && filtered.length === 0 && (
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-10 text-center"
             data-testid="my-approvals-empty">
          <Eye className="w-10 h-10 mx-auto text-zinc-700 mb-3" />
          <h3 className="text-base font-bold text-zinc-300">
            {filter === 'all' ? 'No submissions yet' :
             filter === 'pending' ? 'No pending reviews' :
             filter === 'revise' ? 'Nothing needs revision' :
             'No approvals yet'}
          </h3>
          <p className="text-xs text-zinc-500 mt-1">
            {filter === 'all'
              ? 'Once you submit a task for review, supervisor notes will land here.'
              : 'Switch tabs above to see other submissions.'}
          </p>
        </div>
      )}
      {!loading && filtered.length > 0 && (
        <div className="space-y-1.5" data-testid="my-approvals-list">
          {filtered.map(sub => {
            const lastEventNote = sub.latest_note || sub.last_supervisor_note || sub.last_pm_note || '';
            const commentCount = sub.comment_count || 0;
            const pinCount = sub.pin_count || sub.annotation_count || 0;
            return (
              <button
                key={sub.id}
                data-testid={`my-approvals-row-${sub.id}`}
                onClick={() => setOpenSubmissionId(sub.id)}
                className="w-full text-left bg-zinc-900/60 hover:bg-zinc-900 border border-zinc-800 hover:border-zinc-700 rounded-lg p-3 transition flex items-center gap-3 group"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {_statusPill(sub.status)}
                    {sub.round > 1 && (
                      <span className="text-[9px] font-bold text-amber-300 bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded-full">
                        R{sub.round}
                      </span>
                    )}
                    {sub._origin === 'assignee' && (
                      <span className="text-[9px] text-zinc-500" title="Submitted on your behalf">
                        on your behalf
                      </span>
                    )}
                    {sub.stage && (
                      <span className="text-[9px] uppercase tracking-wider text-zinc-500">
                        {sub.stage}
                      </span>
                    )}
                  </div>
                  <div className="font-semibold text-zinc-100 text-sm truncate">
                    {sub.title || sub.shot_code || sub.id?.slice(0, 8) || 'Submission'}
                  </div>
                  {lastEventNote && (
                    <div className="text-[11px] text-zinc-400 truncate mt-0.5">
                      {lastEventNote}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <div className="flex items-center gap-2 text-[10px] text-zinc-500">
                    {pinCount > 0 && (
                      <span className="flex items-center gap-0.5">
                        <MessageSquare className="w-3 h-3" /> {pinCount}
                      </span>
                    )}
                    {commentCount > 0 && (
                      <span className="flex items-center gap-0.5">
                        <MessageSquare className="w-3 h-3" /> {commentCount}
                      </span>
                    )}
                    <span className="flex items-center gap-0.5">
                      <Calendar className="w-3 h-3" /> {_formatTime(sub.submitted_at).split(',')[0]}
                    </span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-300 transition" />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Detail modal — animator gets SyncSketch side-by-side, others
          get the text + pin-timeline modal */}
      {openSubmissionId && (
        <SubmissionDetailPanel
          submissionId={openSubmissionId}
          useSideBySide={_isAnimatorLike(user)}
          onClose={() => setOpenSubmissionId(null)}
        />
      )}
    </div>
  );
}
